<template>
  <div>
    <b-button variant="info" :disabled="disabled" @click="onShowModal()">
      {{ 'add_attachments_from _disc' | translate('global') }}
    </b-button>

    <Modal ref="select-files"
           @onClose="closeModal"
           :values="modalValues">
      <template slot="body">
        <div v-if="spinners.contentIsLoading" class="text-center">
          <b-spinner type="grow" variant="primary"/>
        </div>
        <div v-else>
          <EmptyTable v-if="!content.ftpFolders.length" :values="emptyDataValues"/>
          <b-row v-else class="flex-container">
            <b-col cols="3">
              <LiquorTree ref="ftp_tree"
                          :data="content.ftpFolders"
                          :options="treeOptions"
                          @node:selected="onNodeSelected"
                          @tree:mounted="onTreeMounted">
                <template slot-scope="{ node }">
                  <span class="tree-text">{{ node.text }}</span>
                </template>
              </LiquorTree>
            </b-col>
            <div class="vl"/>

            <b-col cols="8">
              <b-breadcrumb>
                <b-breadcrumb-item v-for="(item, key) in breadcrumbItems" :key="key"
                                   @click="getData(item.path)">
                  {{ item.text }}
                </b-breadcrumb-item>
              </b-breadcrumb>

              <p class="clickable" v-for="(directory, key) in content.directories" :key="key">
                <i class="fa fa-folder-o lg mr-1" @click="getData(directory.path)">
                  <span class="ml-2">{{ directory.text }}</span>
                </i>
              </p>
              <b-form-group>
                <b-form-checkbox-group
                  v-model="selectedFiles"
                  :options="content.files"
                  name="flavour-2a"
                  stacked>
                </b-form-checkbox-group>
              </b-form-group>
            </b-col>
          </b-row>
        </div>
      </template>

      <template slot="footer">
        <b-button variant="primary" data-dismiss="modal" :disabled="!selectedFiles.length"
                  @click='onAddAttachments()'>
          {{ 'upload_selected_files' | translate('global') }}
        </b-button>
        <b-button variant="secondary" class="ml-2"
                  @click="closeModal()">{{ 'close' | translate('global') }}
        </b-button>
      </template>
    </Modal>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import statuses from '@/store/statuses';
import Modal from '@/views/sharedComponents/Modal';
import VuexTypes from '@/store/types/index';
import forEach from 'lodash.foreach';
import LiquorTree from 'liquor-tree';
import EmptyTable from '@/views/sharedComponents/EmptyTableData';

const FtpTypes = VuexTypes.ftp;
export default {
  mounted() {},
  props: ['disabled'],
  data() {
    return {
      emptyDataValues: {
        text: this.$t('file_system_empty', 'global')
      },
      selectedFiles: [],
      userFtpPathMap: {},
      modalValues: {
        id: 'select-files',
        title: this.$t('select_files', 'global'),
        size: 'xl'
      },
      spinners: {
        contentIsLoading: true,
      },
      content: {
        ftpFolders: [],
        directories: [],
        files: [],
      },
      breadcrumbItems: [],
      activeFtpFolder: null,
      treeOptions: {
        multiple: false,
      },
    };
  },
  components: { Modal, LiquorTree, EmptyTable },
  computed: {
    ...mapGetters('Ftp', [
      'ftpUserList',
    ]),
  },
  watch: {
    ftpUserList: {
      handler: function () {
        if (this.ftpUserList.status === statuses.LOADED) {
          this.spinners.contentIsLoading = false;
          this.fillContent();
        }
      },
      deep: true
    },
  },
  methods: {
    ...mapActions('Ftp', [
      FtpTypes.FTP_USER_LIST,
    ]),
    getData(path) {
      this.spinners.contentIsLoading = true;
      this.breadcrumbItems = [];
      const params = {};
      if (path) {
        params['path'] = path;
      }
      this.FTP_USER_LIST(params);
    },
    fillContent() {
      this.selectedFiles = [];
      const result = {
        ftpFolders: [],
        directories: [],
        files: []
      };
      if (this.ftpUserList.data['user_ftp_folders'].length) {
        if (this.activeFtpFolder === null) {
          this.activeFtpFolder = {
            id: this.ftpUserList.data['user_ftp_folders'][0]['id'],
            path: this.ftpUserList.data['user_ftp_folders'][0]['path'],
          };
        }
        this.selectTreeByFtpFolderId();
        this.makeBreadcrumb();

        forEach(this.ftpUserList.data['user_ftp_folders'], (folder) => {
          result.ftpFolders.push({
            text: folder.path,
            data: { id: folder.id }
          });
        });
        forEach(this.ftpUserList.data['directories'], (directory) => {
          result.directories.push({
            text: directory.name,
            path: directory.path
          });
        });
        forEach(this.ftpUserList.data['files'], (file) => {
          result.files.push({
            text: file.name,
            value: file.path,
          });
        });
      }
      this.content = result;
    },
    makeBreadcrumb() {
      this.breadcrumbItems = [];
      let currentPath = this.ftpUserList.data['current_path'];
      currentPath = currentPath.replace(this.activeFtpFolder.path, '');
      const currentPathSplit = currentPath.split('/');
      this.breadcrumbItems.push({
        text: this.activeFtpFolder.path,
        path: this.activeFtpFolder.path
      });
      let path = this.activeFtpFolder.path;
      forEach(currentPathSplit, (item) => {
        if (item) {
          path += `${item}/`;
          this.breadcrumbItems.push({
            text: item,
            path: path
          });
        }
      });
    },
    selectTreeByFtpFolderId() {
      if (this.$refs.ftp_tree) {
        const selection = this.$refs.ftp_tree.find({
          data: {
            id: Number(this.activeFtpFolder.id)
          }
        });
        if (selection[0]) {
          selection[0].select(true);
        }
      }
    },
    onNodeSelected(node) {
      if (this.activeFtpFolder['id'] !== node.data.id) {
        this.getData(node.data.text);
        this.activeFtpFolder = {
          id: node.data.id,
          path: node.data.text,
        };
      }
    },
    onTreeMounted() {
      this.selectTreeByFtpFolderId();
    },
    onAddAttachments() {
      this.$emit('uploadFiles', this.selectedFiles);
      this.closeModal();
    },
    closeModal() {
      this.$refs['select-files'].hide();
      this.selectedFiles = [];
    },
    onShowModal() {
      this.getData();
      this.$refs['select-files'].show();
    },
  }
};
</script>

<style scoped>
</style>
